import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { animationFrames, find, first, interval, map, skipWhile, startWith, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class UpdateNotificationsService {
  private readonly updates = inject(SwUpdate);
  private readonly destroyRef = inject(DestroyRef);

  manualVersionUpdates$ = this.updates.versionUpdates.pipe(
    skipWhile((value, index) => {
      if (index === 0) return true; // ignore forced angular update on page load
      if (index === 1) return value.type === 'VERSION_READY'; // skip redundant update with "freshness" navigationRequestStrategy https://github.com/angular/angular/issues/54036
      return false;
    })
  );

  public hasUpdates = toSignal(
    this.manualVersionUpdates$.pipe(
      find((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'), // new version is installed
      map(() => true),
      startWith(false)
    )
  );

  public checkForUpdates(): void {
    if (!this.updates.isEnabled) return;

    const fifteenMinutes = 900_000;
    interval(fifteenMinutes)
      .pipe(
        switchMap(() => animationFrames().pipe(first())), // pause while tab is inactive
        switchMap(() => this.updates.checkForUpdate()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
